import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, MapPinIcon, SearchIcon, ShieldCheckIcon, BuildingIcon, ClipboardCheckIcon, ImageIcon, ChevronDownIcon, PhoneIcon, ClockIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'What is a Google Business Profile?',
  answer: 'A Google Business Profile (formerly Google My Business) is a free listing that appears in Google Search and Google Maps when people search for your business or services in your area. It shows your business name, address, phone number, hours, reviews, and more.'
}, {
  question: "Why can't I just set it up myself?",
  answer: "You absolutely can. But many business owners run into issues with verification, choosing the right categories, setting service areas correctly, or getting the profile to actually show up in local searches. We handle the technical details so it's done right the first time."
}, {
  question: 'How long does the setup and verification take?',
  answer: 'Profile creation and optimization can be done within a few days. Verification timing depends on Google - it can take anywhere from a few days to a few weeks depending on the method Google requires for your business (postcard, phone, video, etc.).'
}, {
  question: "Does this guarantee I'll show up in the Google Map Pack?",
  answer: 'No. Map Pack rankings depend on many factors including reviews, proximity, relevance, and website authority. What this service does is make sure your profile is properly set up, verified, and optimized - which is the foundation for local visibility.'
}, {
  question: 'Is this a monthly service?',
  answer: "No, this is a one-time $397 service fee. Once your profile is set up and verified, it's yours. If you want ongoing optimization, review management, or local SEO, we offer those as separate monthly services."
}, {
  question: 'Does Google charge for a Business Profile?',
  answer: "No. Google Business Profiles are free. The $397 is Viridia's service fee for handling the setup, optimization, and verification process for you."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1609">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-1610">
        <span className="text-lg font-semibold text-white" data-id="element-1611">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-1612">
          <ChevronDownIcon className="w-5 h-5" data-id="element-1613" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-1614">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-1615">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1616">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function GbpSetupPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1617">
      <SEOHead title="Google Business Profile Setup & Verification | Phoenix | Viridia Analytics" description="Struggling to get your Google Business Profile verified? We handle the entire setup, optimization, and verification process so your Phoenix business shows up in Google Maps and local searches. $397 one-time." canonical="/google-business-profile-setup" keywords="Google Business Profile setup Phoenix, Google My Business verification, GBP setup service, Google Maps listing Phoenix, local business Google setup, Google Business Profile optimization Phoenix" faq={faqs} data-id="element-1618" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-1619">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1620">
          <div className="absolute top-[-10%] right-[-5%] w-[35%] h-[35%] rounded-full bg-blue-500/10 blur-[120px] animate-pulse-slow" data-id="element-1621"></div>
          <div className="absolute bottom-[-10%] left-[-5%] w-[30%] h-[30%] rounded-full bg-blue-500/10 blur-[100px] animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-1622"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1623">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1624">
            <div className="max-w-2xl" data-id="element-1625">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-1626">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-sm font-semibold tracking-wide mb-6" data-id="element-1627">
                  LOCAL PRESENCE SERVICE
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1628">
                  Google Business Profile{' '}
                  <span className="text-blue-500" data-id="element-1629">Launch & Verification</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1630">
                  If your Google Business Profile isn't set up or verified
                  correctly, you're invisible to local customers searching on
                  Google Maps. We handle the entire process so your business is
                  properly listed, visible, and ready to support reviews, calls,
                  and local trust.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1631">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-1632">
                    Get Started - $397
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1633" />
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-1634">
                    View All Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual — Mock Google Maps Card */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="hidden lg:block" data-id="element-1635">
              <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md mx-auto" data-id="element-1636">
                <div className="flex items-center gap-4 mb-5 pb-4 border-b border-gray-100" data-id="element-1637">
                  <div className="w-14 h-14 bg-viridia/10 rounded-full flex items-center justify-center" data-id="element-1638">
                    <MapPinIcon className="w-7 h-7 text-viridia" data-id="element-1639" />
                  </div>
                  <div data-id="element-1640">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-1641">
                      Your Business Name
                    </h3>
                    <div className="flex items-center text-sm text-gray-600" data-id="element-1642">
                      <span className="text-yellow-500 flex items-center mr-2" data-id="element-1643">
                        5.0 <StarIcon className="w-3 h-3 ml-1 fill-current" data-id="element-1644" />
                      </span>
                      (47 reviews) · Home Services
                    </div>
                  </div>
                </div>
                <div className="space-y-3 mb-5" data-id="element-1645">
                  <div className="flex items-center text-sm text-gray-600" data-id="element-1646">
                    <MapPinIcon className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" data-id="element-1647" />
                    Serving Phoenix, Scottsdale & surrounding areas
                  </div>
                  <div className="flex items-center text-sm text-gray-600" data-id="element-1648">
                    <PhoneIcon className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" data-id="element-1649" />
                    (623) 555-0123
                  </div>
                  <div className="flex items-center text-sm text-green-700 font-medium" data-id="element-1650">
                    <ClockIcon className="w-4 h-4 mr-3 flex-shrink-0" data-id="element-1651" />
                    Open · Closes 6:00 PM
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-1652">
                  <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2.5 rounded-full text-sm font-medium" data-id="element-1653">
                    Website
                  </div>
                  <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2.5 rounded-full text-sm font-medium" data-id="element-1654">
                    Directions
                  </div>
                  <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2.5 rounded-full text-sm font-medium" data-id="element-1655">
                    Call
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 text-center" data-id="element-1656">
                  <span className="text-xs text-gray-400" data-id="element-1657">
                    ✓ Verified on Google
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1658">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1659">
          <ScrollReveal data-id="element-1660">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1661">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1662">
                No Google Profile = No Local Visibility
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1663">
                When someone in Phoenix searches "landscaper near me" or
                "plumber in Scottsdale," Google shows a map with 3 businesses.
                If your profile isn't set up and verified, you're not one of
                them.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1664">
            {[{
            icon: <SearchIcon className="w-7 h-7" data-id="element-1665" />,
            title: "You're Not Showing Up",
            desc: "Without a verified profile, your business doesn't appear in Google Maps or the local map pack - even if you have a great website."
          }, {
            icon: <ShieldCheckIcon className="w-7 h-7" data-id="element-1666" />,
            title: 'Verification Is Confusing',
            desc: "Google's verification process can involve postcards, phone calls, or video walkthroughs. Many business owners get stuck or give up halfway through."
          }, {
            icon: <StarIcon className="w-7 h-7" data-id="element-1667" />,
            title: 'Missing Trust Signals',
            desc: 'An incomplete or unverified profile can\'t collect reviews, display hours, or show up in "near me" searches - costing you jobs every day.'
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-1668">
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full" data-id="element-1669">
                  <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center text-red-400 mb-6" data-id="element-1670">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1671">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1672">{item.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 bg-viridia-black" data-id="element-1673">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1674">
          <ScrollReveal data-id="element-1675">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1676">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1677">
                What's Included
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1678">
                Everything you need to get your Google Business Profile live,
                verified, and optimized - handled for you.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1679">
            {[{
            icon: <BuildingIcon className="w-6 h-6" data-id="element-1680" />,
            title: 'Create or Claim Your Profile',
            desc: 'We create a new profile or claim your existing one so you have full ownership and control.'
          }, {
            icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-1681" />,
            title: 'Verification Management',
            desc: "We guide or manage the entire verification process - whether it's postcard, phone, or video verification."
          }, {
            icon: <ClipboardCheckIcon className="w-6 h-6" data-id="element-1682" />,
            title: 'Correct Business Information',
            desc: 'Business name, address, phone number, and all core details entered accurately for NAP consistency.'
          }, {
            icon: <MapPinIcon className="w-6 h-6" data-id="element-1683" />,
            title: 'Service Area Configuration',
            desc: 'Set your service area and local coverage correctly so you show up in the right neighborhoods and cities.'
          }, {
            icon: <SearchIcon className="w-6 h-6" data-id="element-1684" />,
            title: 'Category & Description Setup',
            desc: 'We choose the right business categories and write a keyword-rich description to maximize your relevance.'
          }, {
            icon: <ClockIcon className="w-6 h-6" data-id="element-1685" />,
            title: 'Hours, Website & Contact Info',
            desc: 'All your operating hours, website URL, phone number, and contact details properly configured.'
          }, {
            icon: <ImageIcon className="w-6 h-6" data-id="element-1686" />,
            title: 'Logo & Image Placeholders',
            desc: 'We upload your logo and branded images if assets are available, or set up placeholders you can update later.'
          }, {
            icon: <CheckCircleIcon className="w-6 h-6" data-id="element-1687" />,
            title: 'Final Live Check',
            desc: 'We verify that your profile is live, properly configured, and appearing correctly in Google Search and Maps.'
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.05} data-id="element-1688">
                <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full group hover:border-viridia/30 transition-colors duration-300" data-id="element-1689">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center text-viridia mb-4 group-hover:bg-viridia/20 transition-colors" data-id="element-1690">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1691">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1692">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PRICING BLOCK */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1693">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1694">
          <ScrollReveal data-id="element-1695">
            <div className="bg-viridia-black rounded-2xl p-8 md:p-12 border border-gray-800 text-center" data-id="element-1696">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1697">
                ONE-TIME SERVICE
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1698">
                $397
              </h2>
              <p className="text-gray-400 mb-2 text-lg" data-id="element-1699">One-time setup fee</p>
              <p className="text-gray-500 text-sm mb-8 max-w-lg mx-auto" data-id="element-1700">
                This is Viridia's service fee for handling the setup,
                optimization, and verification process. Google does not charge
                for creating a Business Profile.
              </p>
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:-translate-y-1" data-id="element-1701">
                Book a Call to Get Started
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1702" />
              </Link>
              <p className="text-gray-500 text-xs mt-6" data-id="element-1703">
                Pairs well with our automation, ad management, and website
                services.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-20 bg-viridia-black" data-id="element-1704">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1705">
          <ScrollReveal data-id="element-1706">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1707">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1708">
                Who This Is For
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1709">
                This service is built for local service business owners who need
                a solid starting point for their online presence.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1710">
            {["New businesses that haven't set up a Google profile yet", 'Businesses that started the process but got stuck on verification', 'Owners who bought a business and need to claim the existing listing', "Contractors who've been relying on word-of-mouth and want to get found online", 'Businesses with an incomplete or outdated profile that needs a fresh start', 'Anyone who just wants it done right without the headache'].map((item, i) => <ScrollReveal key={i} delay={i * 0.05} data-id="element-1711">
                <div className="flex items-start gap-3" data-id="element-1712">
                  <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-1713" />
                  <span className="text-gray-300" data-id="element-1714">{item}</span>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1715">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1716">
          <ScrollReveal data-id="element-1717">
            <div className="text-center mb-16" data-id="element-1718">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1719">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-1720">
            <div className="bg-viridia-black rounded-2xl p-6 md:p-8 border border-gray-800" data-id="element-1721">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1722" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-1723">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1724"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1725">
          <ScrollReveal data-id="element-1726">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1727">
              Ready to Show Up on Google Maps?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1728">
              Book a free call and we'll get your Google Business Profile set
              up, verified, and ready to bring in local customers.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1729">
              Book Your Free Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1730" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}